// Calendar deep-link + .ics generators. All times treated as IST (Asia/Kolkata).
// The .ics timestamps are emitted as UTC (Z suffix) so any calendar app resolves correctly.

const pad = (n) => String(n).padStart(2, "0");

/** Convert an IST wall-clock ISO string (e.g. "2026-07-02T11:00") to a Date in UTC. */
export const istToUtcDate = (istIso) => {
  // Treat the string as IST (UTC+05:30). Build a Date whose UTC epoch corresponds
  // to that wall-clock in IST.
  const [datePart, timePart] = istIso.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = (timePart || "00:00").split(":").map(Number);
  // IST = UTC + 5:30. UTC = IST - 5:30
  return new Date(Date.UTC(y, m - 1, d, hh, mm) - 5.5 * 60 * 60 * 1000);
};

const toIcsUtc = (date) =>
  `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
    date.getUTCDate(),
  )}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(
    date.getUTCSeconds(),
  )}Z`;

const toGoogleUtc = (date) =>
  `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
    date.getUTCDate(),
  )}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(
    date.getUTCSeconds(),
  )}Z`;

const escapeIcsText = (s = "") =>
  String(s)
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

export function buildIcs({ title, description, joinUrl, startIst, durationMinutes }) {
  const start = istToUtcDate(startIst);
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  const dtStamp = toIcsUtc(new Date());
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@channelpartner.network`;
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ChannelPartner.Network//Trainings//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${escapeIcsText(`ChannelPartner.Network Training: ${title}`)}`,
    `DESCRIPTION:${escapeIcsText(`${description || ""}\n\nJoin link: ${joinUrl}`)}`,
    `LOCATION:${escapeIcsText(joinUrl)}`,
    `URL:${joinUrl}`,
    // Three reminders inside the calendar
    "BEGIN:VALARM",
    "TRIGGER:-PT3H",
    "ACTION:DISPLAY",
    "DESCRIPTION:Training in 3 hours",
    "END:VALARM",
    "BEGIN:VALARM",
    "TRIGGER:-PT1H",
    "ACTION:DISPLAY",
    "DESCRIPTION:Training in 1 hour",
    "END:VALARM",
    "BEGIN:VALARM",
    "TRIGGER:-PT10M",
    "ACTION:DISPLAY",
    "DESCRIPTION:Training in 10 minutes",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function downloadIcs(filename, icsContent) {
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".ics") ? filename : `${filename}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function googleCalendarUrl({ title, description, joinUrl, startIst, durationMinutes }) {
  const start = istToUtcDate(startIst);
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `ChannelPartner.Network Training: ${title}`,
    dates: `${toGoogleUtc(start)}/${toGoogleUtc(end)}`,
    details: `${description || ""}\n\nJoin link: ${joinUrl}`,
    location: joinUrl,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function outlookCalendarUrl({ title, description, joinUrl, startIst, durationMinutes }) {
  const start = istToUtcDate(startIst);
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: `ChannelPartner.Network Training: ${title}`,
    body: `${description || ""}\n\nJoin link: ${joinUrl}`,
    location: joinUrl,
    startdt: start.toISOString(),
    enddt: end.toISOString(),
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/** Return details about the slot's live window.
 *  Live window = 10 minutes before start → end of session. */
export function slotWindowState(slotIst, durationMinutes) {
  const start = istToUtcDate(slotIst).getTime();
  const openFrom = start - 10 * 60 * 1000; // 10 min pre-start
  const closeAt = start + durationMinutes * 60 * 1000;
  const now = Date.now();
  if (now < openFrom) return { state: "future", start, openFrom, closeAt };
  if (now >= openFrom && now < start) return { state: "opening", start, openFrom, closeAt };
  if (now >= start && now < closeAt) return { state: "live", start, openFrom, closeAt };
  return { state: "ended", start, openFrom, closeAt };
}

/** Formats slot in a friendly IST wall-clock. */
export function formatSlotIst(slotIst) {
  const d = istToUtcDate(slotIst);
  // Convert back to IST for display
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const wall = `${days[ist.getUTCDay()]}, ${ist.getUTCDate()} ${months[ist.getUTCMonth()]} · ${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())} IST`;
  return wall;
}

/** Client-side mirror of the backend recurring-slot generator (admin preview).
 *  days: [0=Mon .. 6=Sun], times: ["HH:MM"]. Returns upcoming IST ISO strings. */
export function generateRecurringSlots(days, times, durationMinutes = 60, count = 8, horizonDays = 42) {
  if (!days || !days.length || !times || !times.length) return [];
  const daySet = new Set(days.map(Number));
  // A Date shifted +5:30 exposes IST wall-clock via its UTC fields.
  const nowIstShift = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  const out = [];
  for (let i = 0; i <= horizonDays; i++) {
    const d = new Date(nowIstShift.getTime() + i * 86400000);
    const wd = (d.getUTCDay() + 6) % 7; // Mon=0 .. Sun=6
    if (!daySet.has(wd)) continue;
    const y = d.getUTCFullYear();
    const mo = d.getUTCMonth() + 1;
    const da = d.getUTCDate();
    for (const t of times) {
      const [hh, mm] = String(t).split(":").map(Number);
      if (Number.isNaN(hh) || Number.isNaN(mm)) continue;
      const iso = `${y}-${pad(mo)}-${pad(da)}T${pad(hh)}:${pad(mm)}`;
      if (istToUtcDate(iso).getTime() + durationMinutes * 60000 <= Date.now()) continue;
      out.push(iso);
    }
  }
  out.sort();
  return out.slice(0, count);
}